import React from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Box, 
  Paper, 
  Breadcrumbs, 
  Chip,
  Fade,
  Zoom,
  useTheme,
  alpha,
  CardContent,
  IconButton,
  useMediaQuery
} from '@mui/material';
// Using new design system components
import { Typography, Card } from '../../design-system';
import { DESIGN_TOKENS } from '../../design-system/tokens/colors';
import { 
  Home, 
  TrendingUp, 
  AttachMoney, 
  Assignment,
  NavigateNext,
  NavigateBefore,
  Scale,
  LocalDining,
  Business,
  Science,
  AccountBalance,
  Inventory,
  Receipt,
  Group,
  Settings,
  AccountCircle,
  Restaurant,
  CheckCircle,
  LocalHospital,
  HourglassEmpty,
  SearchOff,
  CleaningServices,
  Favorite,
  Park,
  MoreHoriz,
  Replay,
  Cancel,
} from '@mui/icons-material';
import { useDashboard } from '../../contexts/DashboardContext';
import { getViewComponent, getViewMetadata } from '../../modules/registry';
import { useChatContext } from '../../modules/agent';
import { SalesLotView, AvailableLotsList } from '../../modules/marketplace';
import { AgricultureLoading } from '../ui/AgricultureLoading';
import { FinancialDashboard } from '../../modules/farm/components/FinancialDashboard';
import { InvoiceManagement } from '../../modules/farm/components/InvoiceManagement';
import { UserManagementView } from '../../modules/settings';
import { ProfessionalDashboard, Agenda, ServiceBilling } from '../../modules/professional';
import FarmSearchView from '../../modules/agent/components/FarmSearchView';
import FarmManagement from '../../modules/farm/components/FarmManagement';
import { ProfileView } from '../../modules/profile/components/ProfileView';
import LotDetailsView from '../../modules/farm/components/LotDetailsView';
import LotCreatedSuccessView from '../../modules/farm/components/LotCreatedSuccessView';
import AnimalAddedSuccessView from '../../modules/farm/components/AnimalAddedSuccessView';
import AnimalRemovedSuccessView from '../../modules/farm/components/AnimalRemovedSuccessView';
import AnimalAddErrorView from '../../modules/farm/components/AnimalAddErrorView';
import WeighingFeedback from '../../modules/farm/components/WeighingFeedback';
import WeaningFeedback from '../../modules/farm/components/WeaningFeedback';
import BirthFeedback from '../../modules/farm/components/BirthFeedback';
import BirthErrorView from '../../modules/farm/components/BirthErrorView';
import { DeathFeedbackView } from '../../modules/farm/components/DeathFeedbackView';
import { AnimalPassport } from '../../modules/farm/components/AnimalPassport';
import { NutritionView } from '../../modules/farm/components/NutritionView';
import AnimalNutritionHistoryView from '../../modules/farm/components/AnimalNutritionHistoryView';
import LotNutritionHistoryView from '../../modules/farm/components/LotNutritionHistoryView';
import EmptyNutritionHistoryView from '../../modules/farm/components/EmptyNutritionHistoryView';
import { AnimalListView } from '../../modules/farm/components/AnimalListView';
import LotListView from '../../modules/farm/components/LotListView';
import PastureListView from '../../modules/farm/components/PastureListView';
import HealthDetailsView from '../../modules/farm/components/HealthDetailsView';
import LotHealthHistoryView from '../../modules/farm/components/LotHealthHistoryView';
import AnimalHealthHistoryView from '../../modules/farm/components/AnimalHealthHistoryView';
import ReproductiveHistoryView from '../../modules/farm/components/ReproductiveHistoryView';
import PendingOperationView from '../../modules/farm/components/PendingOperationView';
import AnimalNotFoundView from '../../modules/farm/components/AnimalNotFoundView';
import PermissionsPanel from './PermissionsPanel';
import ProductionDetailsView from '../../modules/dairy/components/ProductionDetailsView';
import TeatCleaningDetailsView from '../../modules/dairy/components/TeatCleaningDetailsView';
import AnimalHistoryView from '../../modules/farm/components/AnimalHistoryView';
import NotFoundView from '../../modules/farm/components/NotFoundView';
import ReproductionDetailsView from '../../modules/farm/components/ReproductionDetailsView';
import ErrorView from '../../modules/farm/components/ErrorView';
import BiometricDecisionView from '../../modules/farm/components/BiometricDecisionView';
import BiometricFeedbackView from '../../modules/farm/components/BiometricFeedbackView';
import BiometricCropView from '../../modules/farm/components/BiometricCropView';
import MastitisTestStep1 from '../../modules/laboratory/components/MastitisTestStep1';
import MastitisTutorial from '../../modules/laboratory/components/MastitisTutorial';
import MastitisTestStep2 from '../../modules/laboratory/components/MastitisTestStep2';
import MastitisTestStep3 from '../../modules/laboratory/components/MastitisTestStep3';
import MastitisTeatStep from '../../modules/laboratory/components/MastitisTeatStep';
import ExamsHub from '../../modules/laboratory/components/ExamsHub';
import MastitisAnimalIdStep from '../../modules/laboratory/components/MastitisAnimalIdStep';
import ExamDetail from '../../modules/laboratory/components/ExamDetail';
import AnimalExamList from '../../modules/laboratory/components/AnimalExamList';
import FarmQuickActionsView from '../../modules/farm/components/FarmQuickActionsView';
import NaturalMountFeedback from '../../modules/farm/components/NaturalMountFeedback';
import GenericInfoView from '../../modules/farm/components/GenericInfoView';
import HeatRegistrationView from '../../modules/farm/components/HeatRegistrationView';
import DryingRegistrationView from '../../modules/farm/components/DryingRegistrationView';

import { Loading } from '../ui/Loading';

interface ContentPanelProps {
  contentView: string;
  contentData?: any;
  onIntentUpdate?: (view: string, data?: any) => void;
  onClose?: () => void;
}

export const ContentPanel: React.FC<ContentPanelProps> = ({ contentView, contentData, onIntentUpdate, onClose }) => {
  const data = contentData; // For backward compatibility
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation(['dashboard', 'animal', 'common', 'farm']);
  const { activeSessionId, sessions, organizations, isProcessing, sendMessage, isMultiTaskRunning } = useChatContext();
  const { restorePreviousContent, previousContentView } = useDashboard();
  
  React.useEffect(() => {
    if (contentView === 'multiTaskProgress') {
      const tasks = Array.isArray(contentData?.tasks) ? contentData.tasks : [];
      const statuses: ("pending"|"done"|"error")[] = Array.isArray(contentData?.statuses) ? contentData.statuses : tasks.map(() => "pending");
      const pendingInput = contentData?.pendingInput;
      const allDone = tasks.length > 0 && statuses.length === tasks.length && statuses.every((s) => s === 'done');
      if (contentData?.autoClose && (!tasks.length || allDone) && !pendingInput) {
        onIntentUpdate?.('welcome');
      }
    }
  }, [contentView, contentData, onIntentUpdate]);
  
  // Show loading screen without header when contentView is 'loading'
  if (contentView === 'loading') {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: DESIGN_TOKENS.colors.background.paper,
          borderRadius: 2,
          position: 'relative' // Ensure absolute positioning of Loading works correctly
        }}
      >
        <Loading fullScreen={false} transparentBackground={true} />
      </Box>
    );
  }

  // Get the active session and organization
  const activeSession = sessions.find(s => s.session_id === activeSessionId);
  const organization = organizations.find(org => 
    org.id === activeSession?.organization_id || 
    org._key === activeSession?.organization_id
  );

const getViewInfo = (view: string, orgType?: string) => {
    // Get organization-specific view metadata
    const mappedOrgType = organization?.farm_type || orgType;
    if (mappedOrgType && view !== 'welcome') {
      const metadata = getViewMetadata(mappedOrgType as any, view);
      if (metadata) {
        // Use translation for specific views that have translation keys
        let title = metadata.name;
        if (view === 'animalPassport') {
          title = t('animal:passport.page_title');
        }
        
        return {
          title: title,
          icon: getViewIcon(view, Array.isArray(mappedOrgType) ? mappedOrgType[0] : mappedOrgType),
          breadcrumb: [t('dashboard:sidebar.dashboard'), organization?.name || t('dashboard:labels.organization'), title],
          color: getOrganizationColor(Array.isArray(mappedOrgType) ? mappedOrgType[0] : mappedOrgType)
        };
      }
    }

  // Fallback to generic view info
  switch (view) {
    case 'examsHub':
      return {
        title: t('dashboard:lab.examsHub.title'),
        icon: <Science />,
        breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sections.laboratory'), t('dashboard:lab.examsHub.title')],
        color: '#8B5CF6'
      };
    case 'mastitisTeatStep':
      return {
        title: t('dashboard:lab.mastitis.teatStep.title'),
        icon: <Science />,
        breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sections.laboratory'), t('dashboard:lab.mastitis.teatStep.title')],
        color: '#8B5CF6'
      };
    case 'mastitisIntro':
      return {
        title: t('dashboard:lab.mastitis.intro.title'),
        icon: <Science />,
        breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sections.laboratory'), t('dashboard:lab.mastitis.intro.title')],
        color: '#8B5CF6'
      };
    case 'mastitisTestStep1':
      return {
        title: t('dashboard:lab.mastitis.step1.title'),
        icon: <Science />,
        breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sections.laboratory'), t('dashboard:lab.mastitis.step1.title')],
        color: '#8B5CF6'
      };
    case 'mastitisTestStep2':
      return {
        title: t('dashboard:lab.mastitis.step2.title'),
        icon: <Science />,
        breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sections.laboratory'), t('dashboard:lab.mastitis.step2.title')],
        color: '#8B5CF6'
      };
    case 'mastitisTestStep3':
      return {
        title: t('dashboard:lab.mastitis.step3.title'),
        icon: <img src="/gestores/Laboratório.svg" alt={t('common:alt_texts.laboratory')} style={{ width: 24, height: 24 }} />,
        breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sections.laboratory'), t('dashboard:lab.mastitis.step3.title')],
        color: '#8B5CF6'
      };
    case 'exam-list':
      return {
        title: t('dashboard:lab.examList.title'),
        icon: <Science />,
        breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sections.laboratory'), t('dashboard:lab.examList.title')],
        color: '#8B5CF6'
      };
    case 'exam-detail':
      return {
        title: t('dashboard:lab.examDetail.title'),
        icon: <img src="/gestores/Laboratório.svg" alt={t('common:alt_texts.laboratory')} style={{ width: 24, height: 24 }} />,
        breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sections.laboratory'), t('dashboard:lab.examDetail.title')],
        color: '#8B5CF6'
      };
    case 'animalPassport': {
      const fromLotDetails = data?.previousContentView === 'lotDetails';
      const fromAnimalList = data?.previousContentView === 'animal-list';
      
      let breadcrumb = [t('dashboard:sidebar.dashboard'), t('dashboard:sidebar.animals'), `Animal #${data?.animalId || '127'}`];
      
      if (fromLotDetails) {
        breadcrumb = [t('dashboard:sidebar.dashboard'), t('dashboard:sidebar.lots'), t('dashboard:lots.details.title'), t('animal:passport.page_title')];
      } else if (fromAnimalList) {
        breadcrumb = [t('dashboard:sidebar.dashboard'), t('dashboard:sidebar.animals'), t('farm:animal.farm_list'), t('animal:passport.page_title')];
      }

      return {
        title: t('animal:passport.page_title'),
        icon: <Assignment />,
        breadcrumb,
        color: '#6BC43C'
      };
    }
      case 'animalList':
        return {
          title: t('farm:animal.list'),
          icon: (
            <Box
              component="img"
              src="/vaca.svg"
              alt={t('common:alt_texts.animal')}
              sx={{ width: 20, height: 20 }}
            />
          ),
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sidebar.animals')],
          color: '#6BC43C'
        };
      case 'animal-list':
        return {
          title: t('farm:animal.farm_list'),
          icon: (
            <Box
              component="img"
              src="/vaca.svg"
              alt={t('common:alt_texts.animal')}
              sx={{ width: 20, height: 20 }}
            />
          ),
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sidebar.animals'), t('dashboard:labels.farmList')],
          color: '#6BC43C'
        };
      case 'lotList':
        return {
          title: t('dashboard:sidebar.lots'),
          icon: <img src="/gestores/lote.svg" alt={t('common:entities.lot')} style={{ width: 56, height: 56 }} />,
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sidebar.lots'), t('dashboard:labels.farmList')],
          color: '#6BC43C'
        };
      case 'pastureList':
        return {
          title: t('farm:titles.pasture_list'),
          icon: <img src="/gestores/Pastos.svg" alt={t('common:alt_texts.pastures')} style={{ width: '24px', height: '24px' }} />,
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sidebar.pastures'), t('dashboard:labels.farmList')],
          color: '#6BC43C'
        };
      case 'healthDetails':
        return {
          title: t('dashboard:labels.vaccination'),
          icon: (
            <Box
              component="img"
              src="/gestores/seringa.svg"
              alt={t('common:alt_texts.syringe')}
              sx={{ width: 24, height: 24 }}
            />
          ),
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:labels.healthHistory'), t('dashboard:labels.vaccination')],
          color: '#8B5CF6'
        };
      case 'animalNutritionHistory':
        return {
          title: t('farm:animal.nutritionHistory'),
          icon: (
            <Box
              component="img"
              src="/gestores/nutricao.svg"
              alt={t('common:alt_texts.nutrition')}
              sx={{
                width: 24,
                height: 24,
              }}
            />
          ),
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sidebar.animals'), t('dashboard:labels.nutritionHistory')],
          color: '#6BC43C'
        };
      case 'lotNutritionHistory':
        return {
          title: t('farm:lot.nutritionHistory'),
          icon: (
            <Box
              component="img"
              src="/forragem.png"
              alt={t('common:alt_texts.forage')}
              sx={{
                width: 24,
                height: 24,
              }}
            />
          ),
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sidebar.lots'), t('dashboard:labels.nutritionHistory')],
          color: '#6BC43C'
        };
      case 'lotMilkingHistory':
        return {
          title: t('farm:lot.milkingHistory'),
          icon: (
            <Box
              component="img"
              src="/milk.svg"
              alt={t('common:alt_texts.milk')}
              sx={{
                width: 24,
                height: 24,
              }}
            />
          ),
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sidebar.lots'), t('dashboard:labels.milkingHistory')],
          color: '#4ECDC4'
        };
      case 'animalMilkingHistory':
        return {
          title: t('farm:animal.milkingHistory'),
          icon: (
            <Box
              component="img"
              src="/milk.svg"
              alt={t('common:alt_texts.milk')}
              sx={{
                width: 24,
                height: 24,
              }}
            />
          ),
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sidebar.animals'), t('dashboard:labels.milkingHistory')],
          color: '#4ECDC4'
        };
      case 'milkingHistoryList':
        return {
          title: t('dashboard:labels.milkingHistoryGeneral'),
          icon: (
            <Box
              component="img"
              src="/milk.svg"
              alt={t('common:alt_texts.milk')}
              sx={{
                width: 24,
                height: 24,
              }}
            />
          ),
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sidebar.milking'), t('dashboard:labels.milkingHistoryGeneral')],
          color: '#4ECDC4'
        };
      case 'lotHealthHistory':
        return {
          title: t('dashboard:labels.healthHistory'),
          icon: (
            <Box
              component="img"
              src="/gestores/seringa.svg"
              alt={t('common:alt_texts.syringe')}
              sx={{
                width: 24,
                height: 24,
              }}
            />
          ),
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sidebar.lots'), t('dashboard:labels.healthHistory')],
          color: '#8B5CF6'
        };
      case 'animalHealthHistory':
        return {
          title: t('farm:animal.healthHistory'),
          icon: <LocalHospital />,
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sidebar.animals'), t('dashboard:labels.healthHistory')],
          color: '#8B5CF6'
        };
      case 'reproductiveHistory':
        return {
          title: t('dashboard:labels.reproductiveHistory'),
          icon: (
            <Box
              component="img"
              src="/gestores/reproducao.svg"
              alt={t('dashboard:sections.reproduction')}
              sx={{
                width: 24,
                height: 24,
              }}
            />
          ),
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sidebar.animals'), t('dashboard:labels.reproductiveHistory')],
          color: '#E91E63'
        };
      case 'priceReport':
        return {
          title: t('farm:reports.price'),
          icon: <TrendingUp />,
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sidebar.reports'), t('dashboard:labels.prices')],
          color: '#F59E0B'
        };
      case 'salesList':
        return {
          title: t('farm:sales.list'),
          icon: <AttachMoney />,
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:labels.sales')],
          color: '#10B981'
        };
      case 'carcassReport':
        return {
          title: t('farm:reports.carcass'),
          icon: <Scale />,
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:labels.processing'), t('dashboard:labels.carcass')],
          color: '#FF6B6B'
        };
      case 'milkQualityReport':
        return {
          title: t('farm:quality.milk'),
          icon: <LocalDining />,
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:labels.quality'), t('dashboard:labels.milk')],
          color: '#4ECDC4'
        };
      case 'membershipReport':
        return {
          title: t('farm:reports.membership'),
          icon: <Business />,
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:labels.management'), t('dashboard:labels.members')],
          color: '#45B7D1'
        };
      case 'sales_lot':
        return {
          title: t('dashboard:labels.details'),
          icon: <AttachMoney />,
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sidebar.marketplace'), t('dashboard:labels.lot')],
          color: '#22C55E'
        };
      case 'marketplace_search':
        return {
          title: t('dashboard:sidebar.marketplace'),
          icon: <Business />,
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sidebar.marketplace')],
          color: '#22C55E'
        };
      case 'financialDashboard':
        return {
          title: t('farm:financial.dashboard'),
          icon: <TrendingUp />,
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sections.erp'), t('dashboard:labels.financial')],
          color: '#6BC43C'
        };
      case 'inventoryManagement':
        return {
          title: t('dashboard:erp.inventory.title'),
          icon: <Inventory />,
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sections.erp'), t('dashboard:labels.inventory')],
          color: '#6BC43C'
        };
      case 'pendingOperation':
        return {
          title: t('farm:pendingOperation.title'),
          icon: <HourglassEmpty />,
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('farm:pendingOperation.title')],
          color: '#F59E0B'
        };
      case 'animalNotFound':
        return {
          title: t('farm:animal.notFound'),
          icon: <SearchOff />,
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sidebar.animals'), t('dashboard:labels.notFound')],
          color: '#F59E0B'
        };
      case 'invoiceManagement':
        return {
          title: t('farm:management.invoice'),
          icon: <AttachMoney />,
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sections.erp'), t('dashboard:labels.invoices')],
          color: '#6BC43C'
        };
      case 'businessContacts':
        return {
          title: t('dashboard:erp.businessContacts.title'),
          icon: <Business />,
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sections.erp'), t('dashboard:labels.businessContacts')],
          color: '#6BC43C'
        };
      case 'userManagement':
        return {
          title: t('dashboard:settings.organization.title'),
          icon: <Group />,
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sidebar.settings'), t('dashboard:labels.team')],
          color: '#7C3AED'
        };
      case 'settings':
        return {
          title: t('farm:settings.title'),
          icon: <Settings />,
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sidebar.settings')],
          color: '#64748B'
        };
      case 'farmSearch':
        return {
          title: t('dashboard:labels.search'),
          icon: <Business />,
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sidebar.farms'), t('dashboard:labels.search')],
          color: '#6BC43C'
        };
      case 'farmManagement':
        return {
          title: t('farm:management.farm'),
          icon: <Business />,
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sidebar.farms'), t('dashboard:labels.management')],
          color: '#6BC43C'
        };
      case 'professional_dashboard':
        return {
          title: t('dashboard:sidebar.professional'),
          icon: <Business />,
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sidebar.professional')],
          color: '#8B5CF6'
        };
      case 'agenda':
        return {
          title: t('dashboard:labels.agenda'),
          icon: <Assignment />,
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sidebar.professional'), t('dashboard:labels.agenda')],
          color: '#8B5CF6'
        };
      case 'billing':
        return {
          title: t('dashboard:labels.billing'),
          icon: <AttachMoney />,
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sidebar.professional'), t('dashboard:labels.billing')],
          color: '#8B5CF6'
        };
      case 'nutrition':
        return {
          title: t('dashboard:nutrition.management.title'),
          icon: (
            <Box
              component="img"
              src="/gestores/nutricao.svg"
              alt={t('common:alt_texts.nutrition')}
              sx={{
                width: 24,
                height: 24,
              }}
            />
          ),
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sections.nutrition'), t('dashboard:labels.management')],
          color: '#6BC43C'
        };
      case 'profile':
        return {
          title: t('farm:profile.user'),
          icon: <AccountCircle />,
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:labels.profile')],
          color: '#6366F1'
        };
      case 'lotCreated':
        return {
          title: t('dashboard:labels.created'),
          icon: <CheckCircle />,
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sidebar.lots'), t('dashboard:labels.created')],
          color: '#22c55e'
        };
      case 'weighingFeedback':
        return {
          title: t('dashboard:labels.weighingConfirmed'),
          icon: <Scale />,
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sidebar.animals'), t('dashboard:labels.weighingConfirmed')],
          color: '#22c55e'
        };
      case 'naturalMountFeedback':
        return {
          title: t('dashboard:labels.mountConfirmed'),
          icon: <img src="/vaca.svg" alt={t('common:alt_texts.cow')} style={{ width: '24px', height: '24px' }} />,
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sidebar.animals'), t('dashboard:labels.mountConfirmed')],
          color: '#22c55e'
        };
      case 'weaningFeedback':
        return {
          title: t('dashboard:labels.weaningConfirmed'),
          icon: <img src="/vaca.svg" alt={t('common:alt_texts.cow')} style={{ width: '24px', height: '24px' }} />,
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sidebar.animals'), t('dashboard:labels.weaningConfirmed')],
          color: '#22c55e'
        };
      case 'genericInfo':
        return {
          title: t('dashboard:labels.info'),
          icon: <Assignment />,
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sections.assistant'), t('dashboard:labels.info')],
          color: '#6B7280'
        };
      case 'nascimento':
        return {
          title: t('dashboard:labels.birthConfirmed'),
          icon: <img src="/vaca.svg" alt={t('common:alt_texts.cow')} style={{ width: '24px', height: '24px' }} />,
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sidebar.animals'), t('dashboard:labels.birthConfirmed')],
          color: '#22c55e'
        };
      case 'biometricFeedback':
        return {
          title: t('dashboard:labels.biometrics'),
          icon: <CheckCircle />,
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sidebar.animals'), t('dashboard:labels.biometrics')],
          color: DESIGN_TOKENS.colors.organization.farm
        };
      case 'heatRegistration':
        return {
          title: t('animal:heat_registration.title'),
          icon: (
            <Box
              component="img"
              src="/gestores/reproducao.svg"
              alt={t('dashboard:sections.reproduction')}
              sx={{
                width: 24,
                height: 24,
              }}
            />
          ),
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sidebar.animals'), t('animal:heat_registration.title')],
          color: '#6BC43C'
        };
      case 'dryingRegistration':
        return {
          title: t('animal:drying_registration.title'),
          icon: (
            <Box
              component="img"
              src="/gestores/Ordenha.svg"
              alt={t('dashboard:sidebar.milking')}
              sx={{
                width: 24,
                height: 24,
              }}
            />
          ),
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sidebar.animals'), t('animal:drying_registration.title')],
          color: '#4ECDC4'
        };
      case 'deathFeedback':
        return {
          title: t('dashboard:labels.deathConfirmed'),
          icon: <img src="/vaca.svg" alt={t('common:alt_texts.cow')} style={{ width: '24px', height: '24px' }} />,
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sidebar.animals'), t('dashboard:labels.deathConfirmed')],
          color: '#ef4444'
        };
      case 'permissions':
        return {
          title: t('dashboard:labels.permissions'),
          icon: <CheckCircle />,
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sidebar.settings'), t('dashboard:labels.permissions')],
          color: '#22c55e'
        };
      case 'productionDetails':
        return {
          title: t('dashboard:labels.productionDetails'),
          icon: <img src="/gestores/Ordenha.svg" alt={t('common:alt_texts.milking')} style={{ width: 24, height: 24 }} />,
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sidebar.milking'), t('dashboard:labels.productionDetails')],
          color: '#4ECDC4'
        };
      case 'teatCleaningDetails':
        return {
          title: t('dashboard:labels.teatCleaning'),
          icon: <CleaningServices />,
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sections.handling'), t('dashboard:labels.teatCleaning')],
          color: '#4ECDC4'
        };
      case 'animalHistory':
        return {
          title: t('dashboard:animals.history.title'),
          icon: <Assignment />,
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sidebar.animals'), t('dashboard:animals.history.title')],
          color: '#6BC43C'
        };
      case 'notFound':
        return {
          title: t('dashboard:labels.notFound'),
          icon: <SearchOff />,
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:labels.notFound')],
          color: '#6BC43C'
        };
      case 'reproductionDetails':
        return {
          title: t('dashboard:reproduction.details.title'),
          icon: <img src="/touro.svg" alt={t('common:alt_texts.bull')} style={{ width: '24px', height: '24px' }} />,
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sections.reproduction'), t('dashboard:reproduction.details.title')],
          color: '#6BC43C'
        };
      case 'emptyNutritionHistory':
        return {
          title: t('dashboard:labels.nutritionHistory'),
          icon: (
            <Box
              component="img"
              src="/forragem.png"
              alt={t('common:alt_texts.forage')}
              sx={{
                width: 24,
                height: 24,
              }}
            />
          ),
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sections.nutrition'), t('dashboard:labels.history')],
          color: '#6BC43C'
        };
      case 'lotDetails':
        return {
          title: t('dashboard:lots.details.title'),
          icon: <img src="/gestores/lote.svg" alt={t('common:entities.lot')} style={{ width: '48px', height: '48px' }} />,
          breadcrumb: [t('dashboard:sidebar.dashboard'), t('dashboard:sidebar.lots'), t('dashboard:lots.details.title')],
          color: '#6BC43C'
        };
      default:
        return {
          title: t('dashboard:welcome.heading'),
          icon: <Home />,
          breadcrumb: [t('dashboard:sidebar.dashboard')],
          color: '#0C5240'
        };
    }
  };

  const getViewIcon = (view: string, orgType: string) => {
    switch (view) {
      case 'examsHub': return <Science />;
      case 'mastitisTeatStep': return <Science />;
      case 'mastitisIntro': return <Science />;
      case 'mastitisTestStep1': return <Science />;
      case 'animalPassport': return <Assignment />;
    case 'animalList': return (
      <Box
        component="img"
        src="/vaca.svg"
        alt={t('common:alt_texts.animal')}
        sx={{ width: 20, height: 20 }}
      />
    );
      case 'animal-list': return (
        <Box
          component="img"
          src="/vaca.svg"
          alt={t('common:alt_texts.animal')}
          sx={{ width: 20, height: 20 }}
        />
      );
      case 'lotList': return <img src="/gestores/lote.svg" alt={t('common:entities.lot')} style={{ width: 48, height: 48 }} />;
      case 'healthDetails': return <Science />;
      case 'animalNutritionHistory': return (
        <Box
          component="img"
          src="/forragem.png"
              alt={t('common:alt_texts.forage')}
          sx={{
            width: 24,
            height: 24,
          }}
        />
      );
      case 'lotNutritionHistory': return (
        <Box
          component="img"
          src="/forragem.png"
              alt={t('common:alt_texts.forage')}
          sx={{
            width: 24,
            height: 24,
          }}
        />
      );
      case 'lotHealthHistory': return <LocalHospital />;
      case 'animalHealthHistory': return <LocalHospital />;
      case 'salesList': return <AttachMoney />;
      case 'carcassReport': return <Scale />;
      case 'milkQualityReport': return <LocalDining />;
      case 'membershipReport': return <Business />;
      case 'priceReport': return <TrendingUp />;
      default: return <Home />;
    }
  };

  const getOrganizationColor = (orgType: string) => {
    switch (orgType) {
      case 'farm': return '#6BC43C';
      case 'slaughterhouse': return '#FF6B6B';
      case 'dairy': return '#4ECDC4';
      case 'cooperative': return '#45B7D1';
      default: return '#0C5240';
    }
  };

  const viewInfo = getViewInfo(contentView, Array.isArray(organization?.farm_type) ? organization?.farm_type[0] : organization?.farm_type);

  const renderContent = () => {
    // If no active session and not profile/userManagement view, show welcome
    if ((!activeSession && contentView !== 'profile' && contentView !== 'userManagement') || contentView === 'welcome') {
      return (
        <Fade in timeout={600}>
          <Box>
            <WelcomeContent />
          </Box>
        </Fade>
      );
    }

    // Multitask progress: card centralizado com checklist e formulário de campos faltantes
    if (contentView === 'multiTaskProgress') {
      const tasks: string[] = Array.isArray(data?.tasks) ? data.tasks : [];
      const statuses: ("pending"|"done"|"error")[] = Array.isArray(data?.statuses) ? data.statuses : tasks.map(() => "pending");
      const completedCount = statuses.filter((s) => s === 'done').length;
      const allDone = tasks.length > 0 && completedCount === tasks.length;
      const pendingInput = data?.pendingInput;
      const MAX_VISIBLE = 5;
      const pendingIndices = tasks.map((_, i) => i).filter((i) => statuses[i] === 'pending');
      const visiblePendingIndices = pendingIndices.slice(0, MAX_VISIBLE);
      const remainingCount = Math.max(0, pendingIndices.length - visiblePendingIndices.length);
      return (
        <Fade in timeout={300}>
          <Box sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
            backgroundColor: DESIGN_TOKENS.colors.background.paper,
            borderRadius: 2,
          }}>
            <Card elevation={2} sx={{ maxWidth: 560, width: '100%' }}>
              <CardContent sx={{
                maxHeight: '60vh',
                overflowY: 'auto',
                pr: 1,
                scrollbarWidth: 'thin',
                scrollbarColor: `${alpha('#000', 0.2)} transparent`,
                '&::-webkit-scrollbar': { width: 6 },
                '&::-webkit-scrollbar-track': { background: 'transparent' },
                '&::-webkit-scrollbar-thumb': { backgroundColor: alpha('#000', 0.2), borderRadius: 4 },
                '&::-webkit-scrollbar-thumb:hover': { backgroundColor: alpha('#000', 0.3) },
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Assignment sx={{ color: DESIGN_TOKENS.colors.brand.primary }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>{t('dashboard:multitask.title')}</Typography>
                </Box>
                <Typography sx={{ color: alpha('#000', 0.6), mb: 2 }}>
                  {allDone ? t('dashboard:multitask.allDone') : t('dashboard:multitask.inProgress')}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {tasks.length === 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: alpha('#000', 0.6) }}>
                      <SearchOff sx={{ color: alpha('#000', 0.5) }} />
                      <Typography>{t('dashboard:multitask.none')}</Typography>
                    </Box>
                  )}
                  {visiblePendingIndices.map((globalIdx) => {
                    const task = tasks[globalIdx];
                    const state = statuses[globalIdx];
                    const done = state === 'done';
                    const error = state === 'error';
                    return (
                      <Paper key={globalIdx} variant="outlined" sx={{
                        p: 1.25,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        borderColor: done ? alpha(DESIGN_TOKENS.colors.brand.primary, 0.4) : alpha('#000', 0.1),
                        backgroundColor: done ? alpha(DESIGN_TOKENS.colors.brand.primary, 0.06) : 'transparent',
                      }}>
                        {done ? (
                          <CheckCircle sx={{ color: DESIGN_TOKENS.colors.brand.primary }} />
                        ) : error ? (
                          <Cancel sx={{ color: '#D32F2F' }} />
                        ) : (
                          <HourglassEmpty sx={{ color: alpha('#000', 0.45) }} />
                        )}
                        <Typography sx={{ flex: 1 }}>{task}</Typography>
                        <Chip size="small" label={done ? t('dashboard:multitask.chipDone') : error ? t('dashboard:multitask.chipError') : t('dashboard:multitask.chipPending')} sx={{
                          bgcolor: done ? alpha(DESIGN_TOKENS.colors.brand.primary, 0.12) : error ? alpha('#D32F2F', 0.12) : alpha('#000', 0.06),
                          color: done ? DESIGN_TOKENS.colors.brand.primary : error ? '#D32F2F' : alpha('#000', 0.6),
                          borderRadius: 1,
                        }} />
                        {error && null}
                      </Paper>
                    );
                  })}
                  {remainingCount > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 0.5 }}>
                      <MoreHoriz sx={{ color: alpha('#000', 0.45) }} />
                      <Chip size="small" label={`+${remainingCount}`} sx={{ bgcolor: alpha('#000', 0.06), color: alpha('#000', 0.6), borderRadius: 1 }} />
                    </Box>
                  )}
                </Box>

                {pendingInput && (
                  <Box sx={{ mt: 2, p: 1.5, borderRadius: 1, border: '1px dashed', borderColor: alpha('#000', 0.2) }}>
                    <Typography sx={{ fontWeight: 600, mb: 1 }}>{t('dashboard:multitask.infoNeeded')}</Typography>
                    {pendingInput?.resumo && (
                      <Typography sx={{ color: alpha('#000', 0.7), mb: 1 }}>
                        {pendingInput.resumo}
                      </Typography>
                    )}
                    {!!(Array.isArray(pendingInput?.missing_fields) && pendingInput.missing_fields.length) && (
                      <Typography sx={{ color: alpha('#000', 0.7) }}>
                        {t('dashboard:multitask.missingFieldsLabel')}: {pendingInput.missing_fields.join(', ')}. {t('dashboard:multitask.sendViaChat')}
                      </Typography>
                    )}
                  </Box>
                )}

                {statuses.some(s => s === 'done') && (
                  <Box sx={{ mt: 2 }}>
                    <Typography sx={{ fontWeight: 600, mb: 1 }}>{t('dashboard:multitask.doneTitle', { defaultValue: 'Tarefas concluídas' })}</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {tasks.map((task, idx) => {
                        if (statuses[idx] !== 'done') return null;
                        return (
                          <Paper key={`done-${idx}`} variant="outlined" sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1, borderColor: alpha(DESIGN_TOKENS.colors.brand.primary, 0.4), backgroundColor: alpha(DESIGN_TOKENS.colors.brand.primary, 0.06) }}>
                            <CheckCircle sx={{ color: DESIGN_TOKENS.colors.brand.primary }} />
                            <Typography sx={{ flex: 1 }}>{task}</Typography>
                          </Paper>
                        );
                      })}
                    </Box>
                  </Box>
                )}

                {statuses.some(s => s === 'error') && (
                  <Box sx={{ mt: 2 }}>
                    <Typography sx={{ fontWeight: 600, mb: 1 }}>{t('dashboard:multitask.errorTitle', { defaultValue: 'Tarefas com erro' })}</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {tasks.map((task, idx) => {
                        if (statuses[idx] !== 'error') return null;
                        return (
                          <Paper key={`err-${idx}`} variant="outlined" sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1, borderColor: alpha('#D32F2F', 0.4), backgroundColor: alpha('#D32F2F', 0.06) }}>
                            <Cancel sx={{ color: '#D32F2F' }} />
                            <Typography sx={{ flex: 1 }}>{task}</Typography>
                            
                          </Paper>
                        );
                      })}
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>
        </Fade>
      );
    }

    // Laboratory: Mastitis Test Intro
    if (contentView === 'examsHub') {
      return (
        <Zoom in timeout={400}>
          <Box>
            <ExamsHub />
          </Box>
        </Zoom>
      );
    }
    if (contentView === 'mastitisIntro') {
      return (
        <Zoom in timeout={400}>
          <Box>
            <MastitisTutorial />
          </Box>
        </Zoom>
      );
    }

    // Laboratory: Mastitis Teat selection Step (novo passo inicial)
    if (contentView === 'mastitisTeatStep') {
      return (
        <Zoom in timeout={400}>
          <Box>
            <MastitisTeatStep initialData={data} />
          </Box>
        </Zoom>
      );
    }

    // Laboratory: Mastitis Animal Identification Step (após seleção de tetos)
    if (contentView === 'mastitisAnimalIdStep') {
      return (
        <Zoom in timeout={400}>
          <Box>
            <MastitisAnimalIdStep initialData={data} />
          </Box>
        </Zoom>
      );
    }

    // Laboratory: Mastitis Test Step 1
    if (contentView === 'mastitisTestStep1') {
      return (
        <Zoom in timeout={400}>
          <Box>
            <MastitisTestStep1 onNext={(file, collectionDate) => onIntentUpdate && onIntentUpdate('mastitisTestStep2', { mastitis: { ...(data?.mastitis || {}), plateImageFile: file, collectionDate } })} />
          </Box>
        </Zoom>
      );
    }

    // Laboratory: Mastitis Test Step 2 (seleção de imagens)
    if (contentView === 'mastitisTestStep2') {
      return (
        <Zoom in timeout={400}>
          <Box>
            <MastitisTestStep2 initialData={data} />
          </Box>
        </Zoom>
      );
    }

    // Laboratory: Mastitis Test Step 3 (diagnóstico)
    if (contentView === 'mastitisTestStep3') {
      return (
        <Zoom in timeout={400}>
          <Box>
            <MastitisTestStep3 initialData={data} />
          </Box>
        </Zoom>
      );
    }

    // Laboratory: Exam Detail
    if (contentView === 'exam-list') {
      return (
        <Zoom in timeout={400}>
          <Box>
            <ExamDetail examData={data?.examData} />
          </Box>
        </Zoom>
      );
    }

    // Laboratory: Animal Exam List
    if (contentView === 'exam-detail') {
      return (
        <Zoom in timeout={400}>
          <Box>
            <AnimalExamList examData={data?.examData} />
          </Box>
        </Zoom>
      );
    }

  // Handle marketplace views first
  if (contentView === 'sales_lot' && data?.lotId) {
    return (
      <Zoom in timeout={400}>
        <Box>
          <SalesLotView lotId={data.lotId} />
        </Box>
      </Zoom>
    );
  }

    if (contentView === 'marketplace_search' && data?.searchResult) {
      return (
        <Zoom in timeout={400}>
          <Box>
            <AvailableLotsList 
              searchResult={data.searchResult}
              onLotSelect={(lotId: string) => {
                // This would need to be passed down from parent component
                // For now, we'll just log it
          
              }}
            />
          </Box>
        </Zoom>
      );
    }

    // Handle ERP views for farms
    if (organization?.farm_type) {
      if (contentView === 'financialDashboard') {
        return (
          <Zoom in timeout={400}>
            <Box>
              <FinancialDashboard 
                tenantId={organization._key || organization.id} 
                farmId={data?.farmId || 'FARM001'} 
              />
            </Box>
          </Zoom>
        );
      }
      
      if (contentView === 'invoiceManagement') {
        return (
          <Zoom in timeout={400}>
            <Box>
              <InvoiceManagement 
                tenantId={organization._key || organization.id} 
                farmId={data?.farmId || 'FARM001'} 
              />
            </Box>
          </Zoom>
        );
      }
      
      if (contentView === 'inventoryManagement') {
        return (
          <Zoom in timeout={400}>
            <Box>
              <div style={{ padding: '20px', textAlign: 'center' }}>
                <Typography variant="h6" color="primary">
                  {t('dashboard:erp.inventory.title')}
                </Typography>
                <Typography variant="body2" color="secondary" style={{ marginTop: '10px' }}>
                  {t('dashboard:erp.inventory.subtitle')}
                </Typography>
              </div>
            </Box>
          </Zoom>
        );
      }
      
      if (contentView === 'businessContacts') {
        return (
          <Zoom in timeout={400}>
            <Box>
              <div style={{ padding: '20px', textAlign: 'center' }}>
                <Typography variant="h6" color="primary">
                  {t('dashboard:erp.businessContacts.title')}
                </Typography>
                <Typography variant="body2" color="secondary" style={{ marginTop: '10px' }}>
                  {t('dashboard:erp.businessContacts.subtitle')}
                </Typography>
              </div>
            </Box>
          </Zoom>
        );
      }
      
      if (contentView === 'farmSearch') {
        return (
          <Zoom in timeout={400}>
            <Box>
              <FarmSearchView 
                initialFilters={data?.filters || {}}
                onFarmSelect={(farm) => {
            
                  // This would need to be passed down from parent component
                }}
              />
            </Box>
          </Zoom>
        );
      }
      
      if (contentView === 'farmManagement') {
        return (
          <Zoom in timeout={400}>
            <Box>
              <FarmManagement 
                tenantId={organization?._key || organization?.id}
              />
            </Box>
          </Zoom>
        );
      }
    }

    // Handle professional views
    if (contentView === 'professional_dashboard' || contentView === 'agenda' || contentView === 'billing') {
      const professionalId = data?.professionalId || 'PROF_001'; // In real app, get from user context
      
      const handleProfessionalNavigate = (component: string) => {
        // This would need to be passed down from parent component
  
      };

      if (contentView === 'professional_dashboard') {
        return (
          <Zoom in timeout={400}>
            <Box>
              <ProfessionalDashboard 
                professionalId={professionalId}
                onNavigate={handleProfessionalNavigate}
              />
            </Box>
          </Zoom>
        );
      }
      
      if (contentView === 'agenda') {
        return (
          <Zoom in timeout={400}>
            <Box>
              <Agenda 
                professionalId={professionalId}
                onNavigate={handleProfessionalNavigate}
              />
            </Box>
          </Zoom>
        );
      }
      
      if (contentView === 'billing') {
        return (
          <Zoom in timeout={400}>
            <Box>
              <ServiceBilling 
                professionalId={professionalId}
                onNavigate={handleProfessionalNavigate}
              />
            </Box>
          </Zoom>
        );
      }
    }

    // Handle system-wide views (independent of organization type)
    if (contentView === 'userManagement') {
      return (
        <Zoom in timeout={400}>
          <Box>
            <UserManagementView />
          </Box>
        </Zoom>
      );
    }
    
    if (contentView === 'settings') {
      return (
        <Zoom in timeout={400}>
          <Box>
            <div style={{ padding: '20px', textAlign: 'center' }}>
              <Typography variant="h6" color="primary">
                {t('dashboard:settings.org.title')}
              </Typography>
              <Typography variant="body2" color="secondary" style={{ marginTop: '10px' }}>
                {t('dashboard:settings.org.subtitle')}
              </Typography>
            </div>
          </Box>
        </Zoom>
      );
    }
    
    if (contentView === 'profile') {
      return (
        <Zoom in timeout={400}>
          <Box>
            <ProfileView />
          </Box>
        </Zoom>
      );
    }

    // Handle error view (respostas inesperadas da IA)
    if (contentView === 'errorView' && data?.errorData) {
      return (
        <Zoom in timeout={400}>
          <Box>
            <ErrorView 
              errorData={data.errorData}
              onRetry={() => window.location.reload()}
              onGoHome={() => onIntentUpdate?.('welcome')}
            />
          </Box>
        </Zoom>
      );
    }

    // Handle reproduction details view (detalhes de reprodução)
    if ((contentView === 'reproductionDetails' || 
         data?.url?.includes('/reproducao/detalhes') ||
         data?.url?.includes('/reproducao/inseminacao_artificial')) && data) {
      return (
        <Zoom in timeout={400}>
          <Box>
            <ReproductionDetailsView 
              reproductionData={data}
            />
          </Box>
        </Zoom>
      );
    }

    // Handle not found view (quando algo não é encontrado) - DEVE VIR ANTES de lotDetails
    if ((contentView === 'notFound' || 
         data?.data === null || 
         data?.data?.api_status === 'error') && 
        (data?.resumo?.includes('não encontrado') || 
         data?.resumo?.includes('não foi encontrado') ||
         data?.data?.api_status === 'error')) {
      return (
        <Zoom in timeout={400}>
          <Box>
            <NotFoundView 
              notFoundData={data}
            />
          </Box>
        </Zoom>
      );
    }

    // Handle lot details view
    if (contentView === 'lotDetails' && data) {
      return (
        <Zoom in timeout={400}>
          <Box>
            <LotDetailsView 
              lotData={data}
              onEdit={() => {
          
                // This would need to be passed down from parent component
              }}
              onViewAnimals={(tags) => {
          
                // This would need to be passed down from parent component
              }}
            />
          </Box>
        </Zoom>
      );
    }

    // Handle lot created success view
    if (contentView === 'lotCreated' && data) {
      return (
        <Zoom in timeout={400}>
          <Box>
            <LotCreatedSuccessView 
              lotCreatedData={data}
              onViewDetails={() => {
          
                // This would need to be passed down from parent component
              }}
              onCreateAnother={() => {
          
                // This would need to be passed down from parent component
              }}
            />
          </Box>
        </Zoom>
      );
    }

    // Handle animal added to lot success view
    if (contentView === 'animalAddedToLot' && data) {
      return (
        <Zoom in timeout={400}>
          <Box>
            <AnimalAddedSuccessView 
              animalAddedData={data}
            />
          </Box>
        </Zoom>
      );
    }

    // Handle animal removed from lot success view
    if (contentView === 'animalRemovedFromLot' && data) {
      return (
        <Zoom in timeout={400}>
          <Box>
            <AnimalRemovedSuccessView 
              animalRemovedData={data}
            />
          </Box>
        </Zoom>
      );
    }

    // Handle animal add error view
    if (contentView === 'animalAddError' && data) {
      return (
        <Zoom in timeout={400}>
          <Box>
            <AnimalAddErrorView 
              animalAddErrorData={data}
            />
          </Box>
        </Zoom>
      );
    }

    // Handle weighing feedback view (when weight is registered)
    if ((contentView === 'weighingFeedback' || 
         data?.resumo?.includes('peso registrado') || 
         data?.resumo?.includes('pesagem registrada') ||
         data?.resumo?.includes('peso foi registrado')) && data) {
      return (
        <Zoom in timeout={400}>
          <Box>
            <WeighingFeedback 
              weighingData={data}
            />
          </Box>
        </Zoom>
      );
    }

    // Handle natural mount feedback view (when natural mount is registered)
    if ((contentView === 'naturalMountFeedback' ||
         data?.url?.includes('/reproducao/monta_natural') ||
         data?.resumo?.toLowerCase()?.includes('monta natural registrada')) && data) {
      return (
        <Zoom in timeout={400}>
          <Box>
            <NaturalMountFeedback
              naturalMountData={data}
            />
          </Box>
        </Zoom>
      );
    }

    // Handle weaning feedback view (when weaning is registered)
    if ((contentView === 'weaningFeedback' || 
         data?.resumo?.includes('desmama registrada') || 
         data?.resumo?.includes('Desmama registrada') ||
         data?.data?.content_type === 'desmama') && data) {
      return (
        <Zoom in timeout={400}>
          <Box>
            <WeaningFeedback 
              weaningData={data}
            />
          </Box>
        </Zoom>
      );
    }

    // Handle birth feedback view (when birth is registered)
    if ((contentView === 'nascimento' || 
         data?.resumo?.includes('Nascimento registrado') || 
         data?.resumo?.includes('nascimento registrado') ||
         data?.data?.content_type === 'nascimento') && data) {
      return (
        <Zoom in timeout={400}>
          <Box>
            <BirthFeedback 
              birthData={data}
            />
          </Box>
        </Zoom>
      );
    }

    // Handle birth error view (when mother is not found during birth registration)
    if ((contentView === 'birthError' || 
         data?.data?.content_type === 'nascimento_animal' ||
         data?.data?.title === 'nascimento_animal') && data) {
      return (
        <Zoom in timeout={400}>
          <Box>
            <BirthErrorView 
             data={data}
           />
          </Box>
        </Zoom>
      );
    }

    // Handle death feedback view (when death is registered)
    if ((contentView === 'deathFeedback' || 
         data?.resumo?.includes('óbito registrado') || 
         data?.resumo?.includes('Óbito registrado') ||
         data?.data?.content_type === 'obito') && data) {
      return (
        <Zoom in timeout={400}>
          <Box>
            <DeathFeedbackView 
              data={data}
            />
          </Box>
        </Zoom>
      );
    }

    // Handle heat registration view
    if ((contentView === 'heatRegistration' || 
         data?.operation === 'cio' ||
         data?.data?.operation === 'cio' ||
         data?.resumo?.includes('Registro de Cio')) && data) {
      return (
        <Zoom in timeout={400}>
          <Box>
            <HeatRegistrationView 
              heatData={data}
            />
          </Box>
        </Zoom>
      );
    }

    // Handle drying registration view
    if ((contentView === 'dryingRegistration' || 
         data?.operation === 'secagem' ||
         data?.data?.operation === 'secagem' ||
         data?.resumo?.includes('Secagem registrada')) && data) {
      return (
        <Zoom in timeout={400}>
          <Box>
            <DryingRegistrationView 
              dryingData={data}
            />
          </Box>
        </Zoom>
      );
    }

    // Handle pending operation view (status INCOMPLETO)
    if (data?.status === 'INCOMPLETO' || contentView === 'pendingOperation') {
      return (
        <Zoom in timeout={400}>
          <Box>
            <PendingOperationView 
              pendingData={data}
            />
          </Box>
        </Zoom>
      );
    }

    // Handle animal not found view (content_type cadastro_pendente)
    if (data?.content_type === 'cadastro_pendente' || contentView === 'animalNotFound') {
      return (
        <Zoom in timeout={400}>
          <Box>
            <AnimalNotFoundView 
              animalNotFoundData={data}
            />
          </Box>
        </Zoom>
      );
    }

    // Handle biometric decision view (animal registration with decision options)
    if ((contentView === 'biometricDecision' || (data?.url === '/animal/register' && data?.decisao)) && data && !isMultiTaskRunning) {
      return (
        <Zoom in timeout={400}>
          <Box>
            <BiometricDecisionView decisionData={data} />
          </Box>
        </Zoom>
      );
    }

    // Handle biometric crop view (tela de recorte de biometria)
    if (contentView === 'biometricCrop') {
      return (
        <Zoom in timeout={400}>
          <Box>
            <BiometricCropView cropData={{ cattleTag: data?.cattleTag, animalName: data?.animalName }} />
          </Box>
        </Zoom>
      );
    }

    // Handle biometric feedback view after decision (resumo-only payload)
    if ((contentView === 'biometricFeedback' || (!!data?.resumo && !data?.decisao)) && data) {
      return (
        <Zoom in timeout={400}>
          <Box>
            <BiometricFeedbackView feedbackData={data} />
          </Box>
        </Zoom>
      );
    }

    // Handle production details view (ordenha)
    if ((contentView === 'productionDetails' || data?.url === '/ordenha/producao/detalhes') && data) {
      return (
        <Zoom in timeout={400}>
          <Box>
            <ProductionDetailsView 
              productionData={data}
            />
          </Box>
        </Zoom>
      );
    }

    // Handle teat cleaning details view (limpeza de tetos)
    if ((contentView === 'teatCleaningDetails' || data?.url === '/manejo/limpeza-tetos/detalhes') && data) {
      return (
        <Zoom in timeout={400}>
          <Box>
            <TeatCleaningDetailsView 
              teatCleaningData={data}
            />
          </Box>
        </Zoom>
      );
    }

    // Handle animal history view (histórico do animal)
    if ((contentView === 'animalHistory' || data?.url?.includes('/historico/animal/')) && data) {
      return (
        <Zoom in timeout={400}>
          <Box>
            <AnimalHistoryView 
              animalHistoryData={data}
            />
          </Box>
        </Zoom>
      );
    }

    // Handle permissions feedback view
    if (contentView === 'permissions' && data?.permissionsData) {
      return (
        <Zoom in timeout={400}>
          <Box>
            <PermissionsPanel 
              permissionsData={data.permissionsData}
              operationType={data.operationType}
            />
          </Box>
        </Zoom>
      );
    }

    // Handle health details view
    if (contentView === 'healthDetails' && data?.healthDetailsData) {
      return (
        <Zoom in timeout={400}>
          <Box>
            <HealthDetailsView 
              healthDetailsData={data.healthDetailsData}
            />
          </Box>
        </Zoom>
      );
    }

    // Handle animal passport view
    if (contentView === 'animalPassport') {
      // Prioritize data.animalData if available, otherwise check if data itself is the animal data
      // This fallback is crucial when data comes directly from a service response structure
      const animalData = data?.animalData || (data?.data ? data.data : data);
      
      if (animalData) {
        return (
          <Zoom in timeout={400}>
            <Box>
              <AnimalPassport 
                animalData={animalData}
                previousContentView={data?.previousContentView}
              />
            </Box>
          </Zoom>
        );
      }
    }

    // Handle nutrition view
      if (contentView === 'nutrition' && data?.nutritionData) {
        return (
          <Zoom in timeout={400}>
            <Box>
              <NutritionView 
                nutritionData={data.nutritionData}
              />
            </Box>
          </Zoom>
        );
      }

      // Handle animal nutrition history view
      if (contentView === 'animalNutritionHistory' && data?.animalNutritionData) {
        return (
          <Zoom in timeout={400}>
            <Box>
              <React.Suspense fallback={
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <AgricultureLoading />
                </Box>
              }>
                <AnimalNutritionHistoryView 
                  animalNutritionData={data.animalNutritionData}
                />
              </React.Suspense>
            </Box>
          </Zoom>
        );
      }

      // Handle lot nutrition history view
      if (contentView === 'lotNutritionHistory' && data?.lotNutritionData) {
        return (
          <Zoom in timeout={400}>
            <Box>
              <LotNutritionHistoryView 
                lotNutritionData={data.lotNutritionData}
              />
            </Box>
          </Zoom>
        );
      }

      // Handle empty nutrition history view
      if (contentView === 'emptyNutritionHistory' && data?.emptyNutritionData) {
        return (
          <Zoom in timeout={400}>
            <Box>
              <EmptyNutritionHistoryView 
                emptyNutritionData={data.emptyNutritionData}
              />
            </Box>
          </Zoom>
        );
      }

      // Handle lot health history view
       if (contentView === 'lotHealthHistory' && data?.lotHealthHistoryData) {
         return (
           <Zoom in timeout={400}>
             <Box>
               <LotHealthHistoryView 
                 lotHealthHistoryData={data.lotHealthHistoryData}
               />
             </Box>
           </Zoom>
         );
       }

       // Handle animal health history view
       if (contentView === 'animalHealthHistory' && data?.animalHealthHistoryData) {
         return (
           <Zoom in timeout={400}>
             <Box>
               <AnimalHealthHistoryView 
                 animalHealthHistoryData={data.animalHealthHistoryData}
               />
             </Box>
           </Zoom>
         );
       }

       // Handle reproductive history view
       if (contentView === 'reproductiveHistory' && data?.reproductiveHistoryData) {
         return (
           <Zoom in timeout={400}>
             <Box>
               <ReproductiveHistoryView 
                 reproductiveHistoryData={data.reproductiveHistoryData}
               />
             </Box>
           </Zoom>
         );
       }

          // Handle animal list view
        if (contentView === 'animal-list' && data?.animalListData) {
          return (
            <Zoom in timeout={400}>
              <Box>
                <AnimalListView 
                  animalListData={data.animalListData}
                />
              </Box>
            </Zoom>
          );
        }

        // Handle lot list view
        if (contentView === 'lotList' && data?.lotListData) {
          return (
            <Zoom in timeout={400}>
              <Box>
                <LotListView 
                  lotListData={data.lotListData}
                />
              </Box>
            </Zoom>
          );
        }

        // Handle pasture list view
        if (contentView === 'pastureList' && data?.pastureListData) {
          return (
            <Zoom in timeout={400}>
              <Box>
                <PastureListView 
                  pastureListData={data.pastureListData}
                />
              </Box>
            </Zoom>
          );
        }

        // Handle lot milking history view
        if (contentView === 'lotMilkingHistory' && data?.lotMilkingData) {
          const LotMilkingHistoryView = React.lazy(() => import('../../modules/dairy/components/LotMilkingHistoryView'));
          return (
            <Zoom in timeout={400}>
              <Box>
                <React.Suspense fallback={<AgricultureLoading />}>
                  <LotMilkingHistoryView 
                    lotMilkingData={data.lotMilkingData}
                  />
                </React.Suspense>
              </Box>
            </Zoom>
          );
        }

        // Handle animal milking history view
        if (contentView === 'animalMilkingHistory' && data?.animalMilkingData) {
          const AnimalMilkingHistoryView = React.lazy(() => import('../../modules/dairy/components/AnimalMilkingHistoryView'));
          return (
            <Zoom in timeout={400}>
              <Box>
                <React.Suspense fallback={<AgricultureLoading />}>
                  <AnimalMilkingHistoryView 
                    animalMilkingData={data.animalMilkingData}
                  />
                </React.Suspense>
              </Box>
            </Zoom>
          );
        }

        // Handle general milking history list view
        if (contentView === 'milkingHistoryList' && data?.milkingHistoryListData) {
          const GeneralMilkingHistoryListView = React.lazy(() => import('../../modules/dairy/components/GeneralMilkingHistoryListView'));
          return (
            <Zoom in timeout={400}>
              <Box>
                <React.Suspense fallback={<AgricultureLoading />}>
                  <GeneralMilkingHistoryListView 
                    milkingHistoryListData={data.milkingHistoryListData}
                  />
                </React.Suspense>
              </Box>
            </Zoom>
          );
        }

      // Handle generic info (respostas não padronizadas do assistente)
      if (contentView === 'genericInfo' && data?.genericInfoData) {
        return (
          <Zoom in timeout={400}>
            <Box>
              <GenericInfoView infoData={data.genericInfoData} />
            </Box>
          </Zoom>
        );
      }

      // Get the component from the registry
    // Default to 'farm' type for farm organizations
    const orgType = organization?.farm_type || 'farm';
    const ViewComponent = getViewComponent(orgType as any, contentView);
    
    if (ViewComponent) {
      return <ViewComponent {...data} />;
    }

    // Fallback for unknown views
    return (
      <Fade in timeout={400}>
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ color: '#6B7B90', mb: 1 }}>
            {t('dashboard:errors.contentNotFoundTitle')}
          </Typography>
          <Typography variant="body2" sx={{ color: '#94A3B8' }}>
            {t('dashboard:errors.contentNotFoundSubtitle')}
          </Typography>
        </Box>
      </Fade>
    );
  };

  return (
    <Paper
      elevation={3}
      sx={{
        height: '100%',
        backgroundColor: DESIGN_TOKENS.colors.background.paper,
        borderRadius: 2,
        overflow: 'hidden', // Root container prevents outer scroll
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        border: `1px solid ${DESIGN_TOKENS.colors.primary[200]}`
      }}
    >
      {/* Content Header - Fixed */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${DESIGN_TOKENS.colors.primary[50]} 0%, ${DESIGN_TOKENS.colors.background.paper} 100%)`,
          borderBottom: `1px solid ${DESIGN_TOKENS.colors.primary[200]}`,
          p: 3,
          flexShrink: 0 // Prevent header from shrinking
        }}
      >
        {isMobile && contentView === 'profile' ? (
          // Mobile Profile Header
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: DESIGN_TOKENS.colors.text.primary }}>
              {t('farm:profile.user')}
            </Typography>
            {onClose && (
              <IconButton onClick={onClose} edge="end" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                <Cancel />
              </IconButton>
            )}
          </Box>
        ) : (
          // Default Header
          <>
            {/* Mobile Back Button */}
            {onClose && (
              <Box sx={{ mb: 2 }}>
                <IconButton onClick={onClose} edge="start" sx={{ color: DESIGN_TOKENS.colors.primary[700] }}>
                  <NavigateBefore />
                </IconButton>
              </Box>
            )}

            {/* Breadcrumb Navigation */}
            <Breadcrumbs 
              separator={<NavigateNext fontSize="small" />}
              aria-label="breadcrumb"
              sx={{ mb: 2 }}
            >
              {viewInfo.breadcrumb.map((crumb, index) => {
                const isSecondToLast = index === viewInfo.breadcrumb.length - 2;
                const clickable = (
                  (contentView === 'lotDetails' && isSecondToLast && previousContentView === 'lotList') ||
                  (contentView === 'animalPassport' && isSecondToLast && (data?.previousContentView === 'lotDetails' || data?.previousContentView === 'animal-list'))
                );
                const sxStyles = {
                  color: index === viewInfo.breadcrumb.length - 1 ? DESIGN_TOKENS.colors.primary[700] : DESIGN_TOKENS.colors.text.secondary,
                  fontWeight: index === viewInfo.breadcrumb.length - 1 ? 600 : 500,
                  fontSize: '0.8rem',
                  ...(clickable ? { cursor: 'pointer', '&:hover': { textDecoration: 'underline', color: DESIGN_TOKENS.colors.text.primary } } : {})
                };
                return (
                  <Typography
                    key={index}
                    variant="caption"
                    sx={sxStyles}
                    onClick={clickable ? restorePreviousContent : undefined}
                  >
                    {crumb}
                  </Typography>
                );
              })}
            </Breadcrumbs>

            {/* Title Section */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  backgroundColor: DESIGN_TOKENS.colors.primary[100],
                  color: DESIGN_TOKENS.colors.primary[700],
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {viewInfo.icon}
              </Box>
              <Box>
                <Typography 
                  variant="h5" 
                  component="h1"
                  sx={{ 
                    fontWeight: 700, 
                    color: DESIGN_TOKENS.colors.text.primary,
                    lineHeight: 1.2,
                  }}
                >
                  {viewInfo.title}
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: DESIGN_TOKENS.colors.text.tertiary,
                    display: 'block',
                    mt: 0.5,
                  }}
                >
                  {organization?.name && contentView !== 'welcome' ? `${organization.name} • ` : ''}
                  {t('dashboard:header.updatedRealtime')}
                </Typography>
              </Box>
              
            </Box>
          </>
        )}
      </Box>

      {/* Content Area - Scrollable */}
      <Box sx={{ 
        flex: 1, 
        overflow: 'auto', // Allow scrolling in content area
        minHeight: 0
      }}>
        {renderContent()}
      </Box>
    </Paper>
  );
};

// Welcome content component with proper scrolling
const WelcomeContent: React.FC = () => {
  const { sessions, organizations, activeSessionId, messages } = useChatContext();
  const { t: tDashboard } = useTranslation('dashboard');
  const activeSession = sessions.find(s => s.session_id === activeSessionId);
  const isFarm = activeSession?.organization_type === 'farm';
  const sessionMessages = activeSessionId ? (messages[activeSessionId] || []) : [];
  const isFirstConversation = sessionMessages.length <= 1;

  if (activeSessionId && isFarm && isFirstConversation) {
    return (
      <Box sx={{ 
        height: '100%', 
        overflow: 'auto',
        minHeight: 0,
        '&::-webkit-scrollbar': { width: '8px' },
        '&::-webkit-scrollbar-track': { background: '#f1f1f1' },
        '&::-webkit-scrollbar-thumb': { background: '#c1c1c1', borderRadius: '4px' },
        '&::-webkit-scrollbar-thumb:hover': { background: '#a8a8a8' },
      }}>
        <Box sx={{ p: 4 }}>
          <FarmQuickActionsView />
        </Box>
      </Box>
    );
  }

  const recentSessions = sessions
    .filter(s => s.last_used)
    .sort((a, b) => new Date(b.last_used!).getTime() - new Date(a.last_used!).getTime())
    .slice(0, 3);

  return (
    <Box sx={{ 
      height: '100%', 
      overflow: 'auto', // This enables independent scrolling for content
      minHeight: 0,
      '&::-webkit-scrollbar': {
        width: '8px',
      },
      '&::-webkit-scrollbar-track': {
        background: '#f1f1f1',
      },
      '&::-webkit-scrollbar-thumb': {
        background: '#c1c1c1',
        borderRadius: '4px',
      },
      '&::-webkit-scrollbar-thumb:hover': {
        background: '#a8a8a8',
      },
    }}>
      <Box sx={{ p: 4 }}> {/* Content wrapper without height constraints */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#0C5240', mb: 2 }}>
            {tDashboard('welcome.title')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#6B7B90', mb: 3, maxWidth: 600, mx: 'auto' }}>
            {tDashboard('welcome.subtitle')}
          </Typography>
        </Box>

        {recentSessions.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#0C5240', mb: 2 }}>
              {tDashboard('welcome.recentConversations')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {recentSessions.map((session) => {
                const org = organizations.find(o => o.id === session.organization_id);
                return (
                  <Card key={session.session_id} sx={{ flex: '1 1 300px', maxWidth: 350 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Chip
                          label={org?.name || tDashboard('labels.organization')}
                          size="small"
                          sx={{
                            bgcolor: `${getOrgColor(Array.isArray(org?.farm_type) ? org?.farm_type[0] : org?.farm_type)}20`,
                            color: getOrgColor(Array.isArray(org?.farm_type) ? org?.farm_type[0] : org?.farm_type),
                          }}
                        />
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        {session.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#6B7B90', fontSize: '0.85rem' }}>
                        {tDashboard('labels.lastActivity')}: {session.last_used ? new Date(session.last_used).toLocaleDateString() : tDashboard('labels.recent')}
                      </Typography>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          </Box>
        )}

      </Box>
    </Box>
  );
};

const getOrgColor = (type?: string) => {
  switch (type) {
    case 'farm': return '#6BC43C';
    case 'slaughterhouse': return '#FF6B6B';
    case 'dairy': return '#4ECDC4';
    case 'cooperative': return '#45B7D1';
    default: return '#94A3B8';
  }
};
